import { useState, type FC } from "react";
import Title from "../../atoms/typography/Title";
import { theme } from "../../../theme";
import { useTranslation } from "react-i18next";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Card from "../../atoms/Card";
import CardBody from "../../atoms/CardBody";
import { Col, Row } from "react-grid-system";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import { SortingArrowsIcon } from "../../atoms/icons/SortingArrowsIcon";
import styled from "styled-components";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import Alert from "../../molecules/Alert";
import BodySmall from "../../atoms/typography/BodySmall";
import Small from "../../atoms/typography/Small";
import IconButton from "../../atoms/buttons/IconButton";
import { OpenLinkIcon } from "../../atoms/icons/OpenLinkIcon";
import PaginationComponent from "../../molecules/PaginationComponent";
import OutlinedButton from "../../atoms/buttons/OutlinedButton";
import { RefreshIcon } from "../../atoms/icons/RefreshIcon";
import Tooltip from "../../molecules/Tooltip";
import Body from "../../atoms/typography/Body";
import Checkbox from "../../atoms/inputs/Checkbox";
import { useAllRiskScoreEvents } from "../../../graphql/hooks/aml/useAllRiskScores";
import RiskScoreHitBadge from "../../molecules/aml/RiskScoreHitBadge";
import FilterChip from "../../atoms/inputs/FilterChip";
import TableHeader from "../../atoms/TableHeader";
import TableRow from "../../atoms/TableRow";

const FlexedDivCentered = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  &:hover {
    cursor: pointer;
    filter: opacity(0.6);
  }
`;

const ITEMS_PER_PAGE = 20;

const AmlRiskScores: FC = () => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);

  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [sortBy, setSortBy] = useState<
    "CLIENT_NAME" | "CREATED_AT" | "RISK_SCORE"
  >("CREATED_AT");

  const [showManuallyHandled, setShowManuallyHandled] = useState(false);
  const [selectedRiskScoreClasses, setSelectedRiskScoreClasses] = useState<
    Array<"HIGH" | "MEDIUM" | "NONE" | "NORMAL">
  >(["HIGH", "MEDIUM", "NORMAL"]);

  const [errorMessage, setErrorMessage] = useState("");

  const { riskScores, error, loading, refetch } = useAllRiskScoreEvents(
    sortDirection,
    undefined,
    undefined,
    sortBy,
    showManuallyHandled,
    selectedRiskScoreClasses,
    currentPage * ITEMS_PER_PAGE,
    ITEMS_PER_PAGE,
  );

  const onCheckRiskScoreClass = (
    riskScoreClass: "HIGH" | "MEDIUM" | "NONE" | "NORMAL",
  ) => {
    if (selectedRiskScoreClasses.includes(riskScoreClass)) {
      setSelectedRiskScoreClasses(
        selectedRiskScoreClasses.filter((r) => r !== riskScoreClass),
      );
    } else {
      setSelectedRiskScoreClasses([
        ...selectedRiskScoreClasses,
        riskScoreClass,
      ]);
    }
  };

  const onClickSortByClientName = () => {
    setSortBy("CLIENT_NAME");
    setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
  };
  const onClickSortByRiskScore = () => {
    setSortBy("RISK_SCORE");
    setSortDirection(sortDirection === "ASC" ? "DESC" : "ASC");
  };

  if (error) {
    setErrorMessage(error.message);
  }

  return (
    <div>
      <Title color={theme.colors.primary}>{t("aml.riskScores")}</Title>
      <HorizontalSpacer spacing={2} />
      {errorMessage && <Alert type="error" title={errorMessage} />}
      <FilterChip
        titleKey="aml.displayManuallyEditedScores"
        selected={showManuallyHandled}
        onSelect={() => setShowManuallyHandled(!showManuallyHandled)}
      />
      <HorizontalSpacer spacing={1} />
      <Row justify="between" nogutter align="end">
        <div style={{ display: "flex", gap: 16 }}>
          <Checkbox
            title={t("aml.displayNormalScores")}
            checked={selectedRiskScoreClasses.includes("NORMAL")}
            outlined
            small
            onChange={() => onCheckRiskScoreClass("NORMAL")}
          />
          <Checkbox
            title={t("aml.displayElevatedScored")}
            checked={selectedRiskScoreClasses.includes("MEDIUM")}
            outlined
            small
            onChange={() => onCheckRiskScoreClass("MEDIUM")}
          />
          <Checkbox
            title={t("aml.displayHighRiskScores")}
            checked={selectedRiskScoreClasses.includes("HIGH")}
            outlined
            small
            onChange={() => onCheckRiskScoreClass("HIGH")}
          />
        </div>
        <OutlinedButton
          title={t("common.refresh")}
          onClick={() => refetch()}
          icon={<RefreshIcon color={theme.colors.primary} />}
          color={theme.colors.primary}
        />
      </Row>
      <HorizontalSpacer spacing={1.5} />
      <Card spacing={0} border>
        <TableHeader>
          <Col lg={3}>
            <FlexedDivCentered onClick={onClickSortByClientName}>
              <TableHeaderText>{t("common.client")}</TableHeaderText>
              <SortingArrowsIcon />
            </FlexedDivCentered>
          </Col>
          <Col lg={2}>
            <FlexedDivCentered onClick={onClickSortByRiskScore}>
              <TableHeaderText>{t("aml.riskScores")}</TableHeaderText>
              <SortingArrowsIcon />
            </FlexedDivCentered>
          </Col>

          <Col lg={5}>
            <TableHeaderText>{t("aml.hits")}</TableHeaderText>
          </Col>
        </TableHeader>
        {error && (
          <CardBody>
            <Alert
              type="error"
              title={t("errors.somethingWentWrong")}
              message={error?.toString()}
            />
          </CardBody>
        )}

        {loading ? (
          <CardBody>
            <LoadingSpinner />
          </CardBody>
        ) : (
          <div>
            {riskScores?.items?.map((riskScore) => (
              <TableRow key={`${riskScore.client.id}`} align="center">
                <Col lg={3}>
                  <BodySmall>{riskScore.client.name}</BodySmall>
                  <Small>{riskScore.client.id}</Small>
                </Col>
                <Col lg={2}>
                  <BodySmall>
                    {riskScore.totalScore}p {riskScore.manualRiskScore && "*"}
                  </BodySmall>
                </Col>
                <Col lg={6}>
                  <RiskScoreHitBadge
                    rulesWithHits={
                      riskScore.rules?.filter(
                        (rule) => rule.ruleResult !== "PASSED",
                      ) ?? []
                    }
                    clientIdentifier={riskScore.client.id}
                  />
                </Col>
                <Col lg={1}>
                  <div
                    style={{
                      justifyContent: "flex-end",
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    <Tooltip
                      content={
                        <Body color={theme.colors.white}>
                          {t("documentHandler.goToClientCard")}
                        </Body>
                      }
                    >
                      <IconButton
                        icon={<OpenLinkIcon color={theme.colors.primary} />}
                        color={theme.colors.primary20}
                        onClick={() =>
                          window.open(
                            `/client/${riskScore.client.id}/aml`,
                            "_blank",
                          )
                        }
                      />
                    </Tooltip>
                  </div>
                </Col>
              </TableRow>
            ))}
          </div>
        )}
        <PaginationComponent
          currentPageIndex={currentPage}
          numberOfPages={Math.ceil(
            (riskScores?.totalCount || 0) / ITEMS_PER_PAGE,
          )}
          setCurrentPageIndex={setCurrentPage}
          spacing={1}
        />
      </Card>
    </div>
  );
};

export default AmlRiskScores;
